import { useEffect, useState } from "react";
import {
  AccessibilityInfo,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { useTranslation } from "react-i18next";

import {
  PAIN_SEVERITY_COLOURS,
  painSides,
  worstPainSeverity,
  type BodySide,
  type MuscleSlug,
  type PainMap,
} from "@heraion/shared";

import { DisclosureCard } from "../primitives/DisclosureCard";
import type { UiTheme } from "../theme";
import { DRAWN_MUSCLES, UNSIDED_MUSCLES } from "./drawnMuscles";
import { muscleLabel } from "./muscleLabel";

// The pain map as rows, for whoever cannot aim at the figure (Antenore,
// 30/09): closed by default, not persisted. A row tap is the figure's own
// tap (`onMusclePress`, the picker's cycle), so there is no second logic.

type Row = { slug: MuscleSlug; side?: BodySide };

// One row per half the figure draws, one for a part it draws whole.
const ROWS: ReadonlyArray<Row> = DRAWN_MUSCLES.flatMap((slug): Row[] =>
  UNSIDED_MUSCLES.has(slug) ? [{ slug }] : [{ slug, side: "left" }, { slug, side: "right" }],
);

export function PainMuscleList({
  theme,
  painMap,
  onMusclePress,
}: {
  theme: UiTheme;
  painMap: PainMap;
  onMusclePress: (slug: MuscleSlug, side?: BodySide) => void;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const [touched, setTouched] = useState<Row | null>(null);

  const muscleName = (slug: MuscleSlug) => muscleLabel(t, slug, { splitCore: true });
  const name = (r: Row) =>
    r.side === "left"
      ? t("bodymap.painListLeft", { muscle: muscleName(r.slug) })
      : r.side === "right"
        ? t("bodymap.painListRight", { muscle: muscleName(r.slug) })
        : muscleName(r.slug);
  const severity = (r: Row) =>
    r.side ? painSides(painMap[r.slug])[r.side] : worstPainSeverity(painMap[r.slug]);
  const stateWord = (r: Row) => {
    const sev = severity(r);
    return sev === "red"
      ? t("bodymap.painStateRed")
      : sev === "yellow"
        ? t("bodymap.painStateYellow")
        : null;
  };
  const spoken = (r: Row) =>
    t("bodymap.painListRowA11y", {
      muscle: name(r),
      state: stateWord(r) ?? t("bodymap.painStateNone"),
    });

  // The new state after a tap, said once per platform. TalkBack speaks the
  // focused row's changed label; web readers do not, so the status line is a
  // live region there; iOS gets an explicit announcement.
  const status = touched ? spoken(touched) : "";
  useEffect(() => {
    if (status && Platform.OS === "ios") AccessibilityInfo.announceForAccessibility(status);
  }, [status]);

  return (
    <DisclosureCard
      theme={theme}
      header={<Text style={styles.toggle}>{t("bodymap.painListToggle")}</Text>}
      accessibilityLabel={t("bodymap.painListToggle")}
    >
      <Text
        style={styles.status}
        accessibilityLiveRegion={Platform.OS === "web" ? "polite" : "none"}
      >
        {status}
      </Text>
      {/* No slug-to-zone map exists to group by: sorted by name instead. Root
          collation orders all five locales. */}
      {[...ROWS].sort((a, b) => name(a).localeCompare(name(b))).map((r) => {
        const sev = severity(r);
        const word = stateWord(r);
        return (
          <Pressable
            key={`${r.slug}:${r.side ?? "both"}`}
            accessibilityRole="button"
            accessibilityLabel={spoken(r)}
            onPress={() => {
              onMusclePress(r.slug, r.side);
              setTouched(r);
            }}
            style={({ pressed }) => [styles.row, pressed && styles.pressed]}
          >
            <Text style={styles.name}>{name(r)}</Text>
            {sev === "red" || sev === "yellow" ? (
              <View style={styles.state}>
                <View style={[styles.dot, { backgroundColor: PAIN_SEVERITY_COLOURS[sev] }]} />
                <Text style={styles.word}>{word}</Text>
              </View>
            ) : null}
          </Pressable>
        );
      })}
    </DisclosureCard>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    toggle: { color: p.ink, fontSize: ty.sizes.base, fontWeight: "600" },
    status: { color: p.ink2, fontSize: ty.sizes.sm, paddingTop: s.s2 },
    row: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: s.s3,
      minHeight: 44,
      borderBottomWidth: 1,
      borderBottomColor: p.border,
    },
    pressed: { backgroundColor: p.surface2 },
    name: { color: p.ink, fontSize: ty.sizes.base, flexShrink: 1 },
    state: { flexDirection: "row", alignItems: "center", gap: s.s2 },
    // Same swatch as the legend: the word carries the state, the colour
    // only matches it to the figure.
    dot: { width: 10, height: 10, borderRadius: 5, borderWidth: 1, borderColor: p.borderStrong },
    word: { color: p.ink, fontSize: ty.sizes.base, fontWeight: "600" },
  });
}
